import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import type { Pairing, Player, GameConfig } from '@swiss-magic/shared-types';

export function AddPairingForm({
  players,
  currentPairings,
  gameConfig,
  onAdd,
  onCancel,
}: {
  players: Player[];
  currentPairings: Pairing[];
  gameConfig: GameConfig;
  onAdd: (whitePlayerId: string, blackPlayerId: string | null) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('director');
  const [whitePlayerId, setWhitePlayerId] = useState('');
  const [blackPlayerId, setBlackPlayerId] = useState('');

  // Players already in the current round's pairings
  const pairedPlayerIds = new Set<string>();
  for (const p of currentPairings) {
    pairedPlayerIds.add(p.whitePlayer.id);
    if (p.blackPlayer) pairedPlayerIds.add(p.blackPlayer.id);
  }

  const availablePlayers = players.filter((p) => !pairedPlayerIds.has(p.id));

  // Filter out white player from black options
  const blackOptions = availablePlayers.filter((p) => p.id !== whitePlayerId);

  return (
    <Card className="mt-2">
      <CardContent className="py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-xs text-muted-foreground">{gameConfig.colorNames.first}</label>
            <select
              className="w-full rounded border bg-background px-2 py-1.5 text-sm"
              data-testid="add-pairing-white"
              value={whitePlayerId}
              onChange={(e) => setWhitePlayerId(e.target.value)}
            >
              <option value="">{t('rounds_page.add_pairing.select_player_placeholder')}</option>
              {availablePlayers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName} {p.rating ? `(${p.rating})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs text-muted-foreground">{gameConfig.colorNames.second}</label>
            <select
              className="w-full rounded border bg-background px-2 py-1.5 text-sm"
              data-testid="add-pairing-black"
              value={blackPlayerId}
              onChange={(e) => setBlackPlayerId(e.target.value)}
            >
              <option value="">{t('rounds_page.add_pairing.select_player_placeholder')}</option>
              <option value="bye">{t('rounds_page.add_pairing.bye_option')}</option>
              {blackOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName} {p.rating ? `(${p.rating})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              data-testid="confirm-add-pairing"
              disabled={!whitePlayerId || !blackPlayerId}
              onClick={() => onAdd(whitePlayerId, blackPlayerId === 'bye' ? null : blackPlayerId)}
            >
              {t('rounds_page.add_pairing.add_button')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancel}
            >
              {t('rounds_page.add_pairing.cancel_button')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
